import { useEffect, useRef, useState } from "react";
import type {
  SignageAdSlot,
  SignageBreak,
  SignageItem,
  SignageTimeline,
} from "@shared/types/signage";

/**
 * Vistar ads on the signage wall, ported from the native autoplayer
 * (`nenda-android-native-autoplayer`, `MainActivity.playAdSlot`).
 *
 * The flow is the native one:
 *  1. the schedule has an Ad slot (`timeline.adSlots`, emitted by the resolver
 *     only where an ad is allowed: venue set, place open, nothing live);
 *  2. the panel asks OUR server for the slot (`GET .../adslot`) - the server,
 *     never the panel, talks to Vistar;
 *  3. the ads play one after another, then the loop resumes;
 *  4. each ad that played to its end is reported (`POST .../ad/registerview`)
 *     and the server fires Vistar's proof of play.
 *
 * What is Foyer's own: a filled slot is played AS A CAMPAIGN BREAK. It becomes
 * a `SignageBreak` merged into the timeline, so the surface's break path - the
 * one the soak proved on Samsung, with its layer hand-over, its "a dead item
 * gives the wall back to the loop" rule and its on-disk cache - plays the ad.
 * No second player.
 *
 * The slot is asked for {@link AD_PREFETCH_SECONDS} ahead of its marker: the
 * server asks Vistar for an ad "on screen in 60s", and the lead also lets the
 * creative reach the panel's disk before the marker, exactly like a break item.
 */

// Ad lines are console.warn on purpose: the log shipper sends warn/error and
// `[shaka]`/`[lifecycle]` only, and the `[signage-loop]` diagnostics already
// use warn so an operator can see them in the device logs.

/** Ask this long before a slot's marker. Matches the server's display_time. */
export const AD_PREFETCH_SECONDS = 60;
/** A slot request that has not answered by then is an empty slot. */
const AD_FETCH_TIMEOUT_MS = 10_000;
/** Item ids of ads carry this prefix, so the rest of the surface can tell. */
const AD_ITEM_PREFIX = "vistar-ad-";

/** One ad as `/adslot` hands it to a panel (`SignageAdForScreen`). */
export interface FilledAd {
  adId: string;
  assetUrl: string;
  durationSeconds: number;
  mimeType?: string;
}

/**
 * A filled slot as a break, plus the marker it belongs to. The marker is what
 * pairs it with the campaign break resolved for the same minute.
 */
export type AdBreak = SignageBreak & { slotStartsAt: string };

/** The `/adslot` answer (`SignageAdSlotForScreen`). */
export interface FilledAdSlot {
  numberOfAds: number;
  totalDuration: number;
  ads: FilledAd[];
  reason?: string;
}

const slotStartMs = (slot: SignageAdSlot) => Date.parse(slot.startsAt);

/**
 * The slot to ask for now, if any: the first one whose ask window
 * (`AD_PREFETCH_SECONDS` before its marker, up to its end) holds `now` and
 * that has not been asked for yet. Keyed by `startsAt`, so a slot is asked
 * once however many times the 1 Hz tick passes through its window.
 */
export function adSlotToAsk(
  timeline: Pick<SignageTimeline, "adSlots">,
  now: Date,
  asked: ReadonlySet<string>
): SignageAdSlot | null {
  const t = now.getTime();
  for (const slot of timeline.adSlots ?? []) {
    const start = slotStartMs(slot);
    if (Number.isNaN(start) || asked.has(slot.startsAt)) continue;
    const end = start + Math.max(0, slot.maxSeconds) * 1000;
    if (start - AD_PREFETCH_SECONDS * 1000 <= t && t < end) return slot;
  }
  return null;
}

export function adItemId(adId: string): string {
  return `${AD_ITEM_PREFIX}${adId}`;
}

/** Our ad id behind an ad item's id, or null for any other item. */
export function adIdOfItem(itemId: string): string | null {
  return itemId.startsWith(AD_ITEM_PREFIX)
    ? itemId.slice(AD_ITEM_PREFIX.length)
    : null;
}

/**
 * A filled slot as a campaign break, or null when there is nothing to play.
 *
 * It starts on the slot's marker, or - when the answer came late (a panel that
 * booted mid-slot, a slow request) - on the next whole second after the
 * answer, so the first ad plays from its first frame instead of joining it
 * mid-way. It must END inside the slot: the resolver only promised the wall
 * up to `startsAt + maxSeconds`, and a campaign break may follow right after.
 * A late answer that no longer fits is dropped whole rather than cut, because
 * a cut ad is not a played ad.
 */
export function adBreakFor(
  slot: SignageAdSlot,
  filled: FilledAdSlot,
  answeredAt: Date,
  muted: boolean
): AdBreak | null {
  const ads = (filled.ads ?? []).filter(
    (a) =>
      typeof a.adId === "string" &&
      typeof a.assetUrl === "string" &&
      a.assetUrl !== "" &&
      Number.isFinite(a.durationSeconds) &&
      a.durationSeconds > 0
  );
  if (ads.length === 0) return null;
  const slotStart = slotStartMs(slot);
  if (Number.isNaN(slotStart)) return null;
  const start = Math.max(
    slotStart,
    Math.ceil(answeredAt.getTime() / 1000) * 1000
  );
  const total = ads.reduce((sum, a) => sum + Math.round(a.durationSeconds), 0);
  const slotEnd = slotStart + Math.max(0, slot.maxSeconds) * 1000;
  if (start + total * 1000 > slotEnd) return null;
  return {
    slotStartsAt: slot.startsAt,
    startsAt: new Date(start).toISOString(),
    durationSeconds: total,
    campaignIds: [],
    items: ads.map(
      (a): SignageItem => ({
        id: adItemId(a.adId),
        assetUrl: a.assetUrl,
        // Vistar creatives are progressive MP4s (the server only accepts
        // `video/mp4`): on a Samsung that goes to AVPlay, the same path as a
        // customer's uploaded video in a campaign break.
        kind: "video",
        durationSeconds: Math.round(a.durationSeconds),
        fit: "contain",
        muted,
      })
    ),
  };
}

/**
 * The timeline with filled ad slots added as breaks, and each marker's
 * campaign break moved to start the moment its ads end - ads, then campaign,
 * as one interruption (Sigge, 2026-10-20). Same object when there are none.
 *
 * A campaign break already ON the wall is never pushed back: when the ads
 * answer came only after the marker (a late or slow request), the campaign has
 * started on the minute and those ads are dropped rather than cutting in.
 */
export function withAdBreaks(
  timeline: SignageTimeline,
  adBreaks: readonly AdBreak[]
): SignageTimeline {
  if (adBreaks.length === 0) return timeline;
  const atMarker = new Map<number, AdBreak>();
  for (const a of adBreaks) atMarker.set(Date.parse(a.slotStartsAt), a);
  const playable: AdBreak[] = [];
  const paired = new Set<AdBreak>();
  const breaks = timeline.breaks.map((b) => {
    const ad = atMarker.get(Date.parse(b.startsAt));
    if (!ad) return b;
    paired.add(ad);
    if (Date.parse(ad.startsAt) > Date.parse(ad.slotStartsAt)) return b;
    playable.push(ad);
    const end = Date.parse(ad.startsAt) + ad.durationSeconds * 1000;
    return { ...b, startsAt: new Date(end).toISOString() };
  });
  // A marker with no campaign break (5 an hour, or campaigns off) plays its
  // ads alone, late answers included.
  for (const a of adBreaks) if (!paired.has(a)) playable.push(a);
  return { ...timeline, breaks: [...playable, ...breaks] };
}

/** Each ad item's window in a break: [startMs, endMs). */
export function adItemWindows(
  brk: SignageBreak
): { adId: string; itemId: string; startMs: number; endMs: number }[] {
  const out: {
    adId: string;
    itemId: string;
    startMs: number;
    endMs: number;
  }[] = [];
  let at = Date.parse(brk.startsAt);
  for (const item of brk.items) {
    const end = at + Math.max(0, item.durationSeconds) * 1000;
    const adId = adIdOfItem(item.id);
    if (adId) out.push({ adId, itemId: item.id, startMs: at, endMs: end });
    at = end;
  }
  return out;
}

export interface VistarAdsInput {
  screenId: string;
  /** The panel's screen token. Null on the CMS preview: no ads there. */
  token: string | null;
  apiBase: string;
  timeline: SignageTimeline | null;
  /** The surface's own (server-corrected) clock. */
  clock: () => Date;
  /** Bumps once a second; drives the checks. */
  tick: number;
  /** The item on the wall as of the last render, or null. */
  currentItemId: string | null;
  /** Items that failed to play and are being skipped. */
  deadIds: ReadonlySet<string>;
}

/**
 * Fills ad slots and reports the ads that played. Returns the filled slots as
 * breaks, for {@link withAdBreaks}.
 *
 * An ad counts as played when it was on the wall and still alive when its
 * window ended - the native player's "completed" callback, read off the clock
 * like everything else in this surface. A dead ad is never reported.
 */
export function useVistarAds({
  screenId,
  token,
  apiBase,
  timeline,
  clock,
  tick,
  currentItemId,
  deadIds,
}: VistarAdsInput): AdBreak[] {
  const [adBreaks, setAdBreaks] = useState<AdBreak[]>([]);
  const asked = useRef<Set<string>>(new Set());
  const shown = useRef<Set<string>>(new Set());
  const reported = useRef<Set<string>>(new Set());
  const clockRef = useRef(clock);
  clockRef.current = clock;

  // A different screen or credential starts from nothing.
  useEffect(() => {
    asked.current = new Set();
    shown.current = new Set();
    reported.current = new Set();
    setAdBreaks([]);
  }, [screenId, token]);

  // Ask for the slot whose window has opened.
  useEffect(() => {
    if (!token || !timeline) return;
    const now = clockRef.current();
    const slot = adSlotToAsk(timeline, now, asked.current);
    if (!slot) return;
    asked.current.add(slot.startsAt);
    const muted = !timeline.soundEnabled;
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), AD_FETCH_TIMEOUT_MS);
    void (async () => {
      try {
        const res = await fetch(
          `${apiBase}/api/signage/v1/screens/${encodeURIComponent(
            screenId
          )}/adslot`,
          { headers: { "X-Screen-Token": token }, signal: ctrl.signal }
        );
        if (!res.ok) {
          console.warn(
            `[signage-loop] AD slot ${slot.startsAt}: http ${res.status}`
          );
          return;
        }
        const filled = (await res.json()) as FilledAdSlot;
        const brk = adBreakFor(slot, filled, clockRef.current(), muted);
        console.warn(
          `[signage-loop] AD slot ${slot.startsAt}: ${
            brk
              ? `${brk.items.length} ad(s), ${brk.durationSeconds}s from ${brk.startsAt}`
              : `empty (${filled.reason ?? (filled.numberOfAds > 0 ? "late" : "no ads")})`
          }`
        );
        if (!brk) return;
        setAdBreaks((prev) => [
          // Ended breaks go: they are history, and the list stays tiny.
          // Kept well past their own end: the campaign break they pushed back
          // still needs them to know where it starts. The next slot is six
          // minutes on, so three is plenty.
          ...prev.filter(
            (b) =>
              Date.parse(b.slotStartsAt) + 3 * 60_000 >
              clockRef.current().getTime()
          ),
          brk,
        ]);
      } catch (err) {
        console.warn(
          `[signage-loop] AD slot ${slot.startsAt}: ${
            (err as Error).name === "AbortError"
              ? "timed out"
              : (err as Error).message
          }`
        );
      } finally {
        window.clearTimeout(timer);
      }
    })();
    // No abort on cleanup: a re-render must not cancel a slot already asked.
  }, [tick, token, timeline, screenId, apiBase]);

  // Proof of play.
  useEffect(() => {
    if (!token) return;
    const adId = currentItemId ? adIdOfItem(currentItemId) : null;
    if (adId && currentItemId && !deadIds.has(currentItemId)) {
      shown.current.add(adId);
    }
    const nowMs = clockRef.current().getTime();
    for (const brk of adBreaks) {
      for (const w of adItemWindows(brk)) {
        if (nowMs < w.endMs || reported.current.has(w.adId)) continue;
        if (!shown.current.has(w.adId) || deadIds.has(w.itemId)) continue;
        reported.current.add(w.adId);
        console.warn(`[signage-loop] AD played ${w.adId}`);
        void fetch(
          `${apiBase}/api/signage/v1/screens/${encodeURIComponent(
            screenId
          )}/ad/registerview`,
          {
            method: "POST",
            headers: {
              "X-Screen-Token": token,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ adId: w.adId }),
          }
        ).catch((err: Error) =>
          console.warn(`[signage-loop] AD report ${w.adId}: ${err.message}`)
        );
      }
    }
  }, [tick, token, currentItemId, deadIds, adBreaks, screenId, apiBase]);

  return adBreaks;
}
